// Owns the shared accessible live region and semantic markup for browser notices.
import { Log } from './logger'
import { toastDetailLabels, type ToastDetails } from './toast-details'

export type NotificationKind = 'success' | 'error'
export type NotificationSource = 'action' | 'frontend'

type NotificationOptions = {
  kind: NotificationKind
  source: NotificationSource
  lifetimeMs: number
  dismissLabel: string
  details?: ToastDetails
  detailsLabel?: string
}

const ACTION_TOAST_LIFETIME_MS = 10_000
const observedDialogs = new WeakSet<HTMLDialogElement>()

type ToastLabels = {
  dismiss: string
  details: string
}

let cachedToastLabels: ToastLabels | undefined

/**
 * Reads the localized toast button labels the layout renders on <body>, once per page.
 */
export function toastLabels(): ToastLabels {
  cachedToastLabels ??= {
    dismiss: document.body?.dataset.toastDismissLabel ?? '',
    details: document.body?.dataset.toastDetailsLabel ?? '',
  }

  return cachedToastLabels
}

/** Displays one localized action result through the shared toast component. */
export function showActionNotification(
  message: string,
  kind: NotificationKind,
  details?: ToastDetails,
  detailsLabel?: string,
): HTMLElement {
  return showNotification(message, {
    kind,
    source: 'action',
    lifetimeMs: ACTION_TOAST_LIFETIME_MS,
    dismissLabel: toastLabels().dismiss,
    details,
    detailsLabel,
  })
}

/**
 * Renders one dismissible notice and returns its element for focused assertions.
 */
export function showNotification(
  message: string,
  options: NotificationOptions,
): HTMLElement {
  const description = `${options.source} ${options.kind}`

  return Log.notice(`Display ${description} browser notification`, () => {
    const region = notificationRegion()
    const signature = `${options.source}:${options.kind}:${message}:${options.details?.reference ?? ''}`
    const existing = Array.from(region.querySelectorAll<HTMLElement>('.app-toast'))
      .find((candidate) => candidate.dataset.toastSignature === signature)

    if (existing !== undefined) {
      return existing
    }

    const toast = document.createElement('div')
    toast.className = 'app-toast'
    toast.dataset.toastKind = options.kind
    toast.dataset.toastSignature = signature

    if (options.source === 'action') {
      toast.dataset.actionToast = ''
    } else {
      toast.dataset.frontendToast = ''
    }

    toast.setAttribute('role', options.kind === 'error' ? 'alert' : 'status')

    const icon = document.createElement('span')
    icon.className = 'app-toast-icon'
    icon.ariaHidden = 'true'
    icon.textContent = options.kind === 'success' ? '✓' : '!'

    const content = document.createElement('div')
    content.className = 'app-toast-content'

    const text = document.createElement('div')
    text.className = 'app-toast-text'
    text.textContent = message

    content.append(text)

    const fields = detailFields(options.details)

    if (fields.length > 0) {
      const details = document.createElement('details')
      details.className = 'app-toast-details'

      const summary = document.createElement('summary')
      summary.textContent = options.detailsLabel || toastLabels().details

      const list = document.createElement('dl')
      list.className = 'app-toast-technical'

      fields.forEach(([label, value]) => {
        const term = document.createElement('dt')
        term.textContent = label

        const description = document.createElement('dd')
        description.textContent = value

        list.append(term, description)
      })

      details.append(summary, list)
      content.append(details)
    }

    const close = document.createElement('button')
    close.type = 'button'
    close.className = 'app-toast-close'
    close.setAttribute('aria-label', options.dismissLabel)
    close.textContent = '×'
    close.addEventListener('click', () => toast.remove())

    toast.append(icon, content, close)
    region.appendChild(toast)

    if (options.kind !== 'error') {
      window.setTimeout(() => toast.remove(), options.lifetimeMs)
    }

    return toast
  })
}

/** Returns the label and value of each present details field, in a fixed order. */
function detailFields(details?: ToastDetails): Array<[string, string]> {
  if (details === undefined) return []

  const labels = toastDetailLabels()

  return (['reference', 'provider', 'category', 'status'] as const)
    .filter((field) => details[field] !== undefined)
    .map((field) => [labels[field], String(details[field])])
}

/**
 * Returns the one page-level live region shared by success and error notices.
 */
function notificationRegion(): HTMLElement {
  const existing = document.getElementById('frontend-toast-region')

  if (existing !== null) {
    hostNotificationRegion(existing)

    return existing
  }

  const region = document.createElement('div')
  region.id = 'frontend-toast-region'
  region.className = 'app-toast-region'
  region.setAttribute('aria-live', 'polite')
  region.setAttribute('aria-atomic', 'false')

  hostNotificationRegion(region)

  return region
}

/**
 * Moves an existing notification region above the open modal dialog, or back to the page.
 */
export function rehostNotificationRegion(): void {
  const existing = document.getElementById('frontend-toast-region')

  if (existing !== null) {
    hostNotificationRegion(existing)
  }
}

/**
 * Hosts the region in the open modal dialog so toasts stay above it and clickable.
 */
function hostNotificationRegion(region: HTMLElement): void {
  const openDialogs = document.querySelectorAll<HTMLDialogElement>('dialog[open]')
  const focusedDialog = document.activeElement?.closest<HTMLDialogElement>('dialog[open]') ?? null
  const lastDialog = openDialogs.length > 0 ? openDialogs[openDialogs.length - 1] : null
  const dialog = focusedDialog ?? lastDialog ?? null
  const host = dialog ?? document.body ?? document.documentElement

  if (region.parentElement !== host) {
    host.appendChild(region)
  }

  if (dialog === null || observedDialogs.has(dialog)) {
    return
  }

  observedDialogs.add(dialog)
  dialog.addEventListener('close', () => {
    observedDialogs.delete(dialog)

    if (dialog.contains(region)) {
      hostNotificationRegion(region)
    }
  }, { once: true })
}
